import React, { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
    createChatConversation,
    getChatConversation,
    sendChatMessage,
} from "../api/OtterApi";
import "./BottomUtteranceBar.css";

const CONVERSATION_STORAGE_KEY = "lb-client-web-bottom-utterance-conversation-id";
const COLLAPSED_CONVERSATION_STORAGE_KEY = "lb-client-web-conversation-collapsed";
const MAX_TRANSCRIPT_ITEMS = 20;

const formatLocalCompactTime = (timestamp) => {
    if (!timestamp) {
        return "";
    }

    return new Date(timestamp).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
    });
};

const normalizeApiMessage = (message) => {
    if (!message || typeof message.content !== "string") {
        return null;
    }

    return {
        sender: message.sender === "user" ? "me" : "lifebespoked",
        text: message.content,
        timestamp: Date.parse(message.created_at) || Date.now(),
    };
};

const getInitialCollapsedState = () => {
    try {
        const savedState = sessionStorage.getItem(COLLAPSED_CONVERSATION_STORAGE_KEY);
        if (savedState === "true") {
            return true;
        }

        if (savedState === "false") {
            return false;
        }
    } catch (error) {
        // Default to collapsed if storage is unavailable.
    }

    return true;
};

const BottomUtteranceBar = ({ maxChars = 140, onSubmit }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [utterance, setUtterance] = useState("");
    const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingTranscript, setIsLoadingTranscript] = useState(true);
    const [conversationId, setConversationId] = useState(null);
    const transcriptScrollRef = useRef(null);
    const [transcriptItems, setTranscriptItems] = useState([]);

    const trimmedUtterance = utterance.trim();
    const isOverLimit = utterance.length > maxChars;
    const canSubmit =
        trimmedUtterance.length > 0 &&
        !isOverLimit &&
        !isSubmitting &&
        !isLoadingTranscript;

    const getToken = async () => {
        return await getAccessTokenSilently();
    };

    const createConversation = async () => {
        const token = await getToken();
        const response = await createChatConversation(token);

        if (response.status < 200 || response.status >= 300) {
            throw new Error("Failed to create chat conversation");
        }

        const nextConversationId = response.data?.conversation_id;
        if (!nextConversationId) {
            throw new Error("Missing conversation id from chat/new response");
        }

        localStorage.setItem(CONVERSATION_STORAGE_KEY, nextConversationId);
        setConversationId(nextConversationId);
        return nextConversationId;
    };

    const loadTranscript = async (currentConversationId) => {
        const token = await getToken();
        const response = await getChatConversation(token, currentConversationId);

        if (response.status < 200 || response.status >= 300) {
            throw new Error("Failed to fetch chat transcript");
        }

        const messages = response.data?.conversation?.messages || [];
        const normalizedMessages = messages
            .map(normalizeApiMessage)
            .filter(Boolean)
            .slice(-MAX_TRANSCRIPT_ITEMS);

        setTranscriptItems(normalizedMessages);
    };

    useEffect(() => {
        let isMounted = true;

        const initializeConversation = async () => {
            setIsLoadingTranscript(true);
            try {
                const storedConversationId = localStorage.getItem(
                    CONVERSATION_STORAGE_KEY,
                );

                const currentConversationId =
                    storedConversationId || (await createConversation());

                if (!isMounted) {
                    return;
                }

                setConversationId(currentConversationId);
                await loadTranscript(currentConversationId);
            } catch (error) {
                if (isMounted) {
                    setTranscriptItems([]);
                    setConversationId(null);
                    localStorage.removeItem(CONVERSATION_STORAGE_KEY);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingTranscript(false);
                }
            }
        };

        initializeConversation();

        return () => {
            isMounted = false;
        };
    }, [getAccessTokenSilently]);

    useEffect(() => {
        if (transcriptScrollRef.current) {
            transcriptScrollRef.current.scrollTop =
                transcriptScrollRef.current.scrollHeight;
        }
    }, [transcriptItems]);

    useEffect(() => {
        sessionStorage.setItem(COLLAPSED_CONVERSATION_STORAGE_KEY, String(isCollapsed));
    }, [isCollapsed]);

    const submitUtterance = async () => {
        if (!canSubmit) {
            return;
        }

        setIsSubmitting(true);
        try {
            const currentConversationId = conversationId || (await createConversation());
            const token = await getToken();
            const response = await sendChatMessage(
                token,
                currentConversationId,
                trimmedUtterance,
            );

            if (response.status < 200 || response.status >= 300) {
                throw new Error("Failed to send chat message");
            }

            const responseMessages = [
                response.data?.message,
                ...(response.data?.agent_messages || []),
            ]
                .map(normalizeApiMessage)
                .filter(Boolean);

            if (responseMessages.length > 0) {
                setTranscriptItems((existingItems) =>
                    [...existingItems, ...responseMessages].slice(
                        -MAX_TRANSCRIPT_ITEMS,
                    ),
                );
            }

            if (onSubmit) {
                onSubmit(trimmedUtterance);
            }

            setUtterance("");
        } catch (error) {
            // Keep existing transcript visible when message submission fails.
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === "Escape") {
            event.currentTarget.blur();
            return;
        }

        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submitUtterance();
        }
    };

    const clearTranscript = async () => {
        setTranscriptItems([]);

        try {
            setIsLoadingTranscript(true);
            await createConversation();
        } catch (error) {
            setConversationId(null);
            localStorage.removeItem(CONVERSATION_STORAGE_KEY);
        } finally {
            setIsLoadingTranscript(false);
        }
    };

    if (isCollapsed) {
        return (
            <button
                className="bottom-utterance-fab"
                type="button"
                onClick={() => setIsCollapsed(false)}
                aria-label="Open chat"
                title="Open chat"
            >
                <span className="bottom-utterance-fab-label" aria-hidden="true">
                    LB
                </span>
            </button>
        );
    }

    return (
        <div
            className="bottom-utterance-shell"
            role="region"
            aria-label="Utterance input"
        >
            <section
                className="bottom-utterance-transcript"
                aria-label="Utterance transcript"
            >
                <div
                    ref={transcriptScrollRef}
                    className="bottom-utterance-transcript-scroll"
                >
                    <div className="bottom-utterance-transcript-title">
                        <span>AI Assistant</span>
                        <div className="bottom-utterance-transcript-actions">
                            <button
                                className="bottom-utterance-transcript-clear-button"
                                type="button"
                                onClick={clearTranscript}
                                aria-label="Clear transcript"
                                title="Clear transcript"
                                disabled={transcriptItems.length === 0}
                            >
                                Clear
                            </button>
                            <button
                                className="bottom-utterance-transcript-close-button"
                                type="button"
                                onClick={() => setIsCollapsed(true)}
                                aria-label="Close"
                                title="Close"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                    {transcriptItems.length === 0 ? (
                        <div className="bottom-utterance-transcript-empty">
                            {isLoadingTranscript
                                ? "Loading messages..."
                                : "No messages sent yet."}
                        </div>
                    ) : (
                        <ol className="bottom-utterance-transcript-list">
                            {transcriptItems.map((item, index) => (
                                <li
                                    key={`${item.sender}-${item.text}-${index}`}
                                    className={`bottom-utterance-transcript-item bottom-utterance-transcript-item-${item.sender}`}
                                >
                                    <div className="bottom-utterance-transcript-bubble">
                                        <div className="bottom-utterance-transcript-meta">
                                            <span className="bottom-utterance-transcript-sender">
                                                {item.sender === "lifebespoked"
                                                    ? "lifebespoked"
                                                    : "me"}
                                            </span>
                                            <span className="bottom-utterance-transcript-timestamp">
                                                {formatLocalCompactTime(
                                                    item.timestamp,
                                                )}
                                            </span>
                                        </div>
                                        <div>{item.text}</div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </section>
            <div className="bottom-utterance-pill">
                <input
                    className="bottom-utterance-input"
                    type="text"
                    value={utterance}
                    placeholder="Type an utterance"
                    onChange={(event) => setUtterance(event.target.value)}
                    onKeyDown={handleKeyDown}
                    aria-label="Type an utterance"
                />
                <div
                    className={`bottom-utterance-counter${isOverLimit ? " bottom-utterance-counter-over" : ""}`}
                    aria-live="polite"
                >
                    {utterance.length}/{maxChars}
                </div>
                <button
                    className="bottom-utterance-send"
                    type="button"
                    onClick={submitUtterance}
                    disabled={!canSubmit}
                    aria-label="Send utterance"
                >
                    {isSubmitting ? "Sending..." : "Send"}
                </button>
            </div>
        </div>
    );
};

export default BottomUtteranceBar;
